import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, LoadingSkeleton, Modal, ToastContainer } from '../ui';
import { EyeIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../../utils/apiError';
import { hasAnyRole } from '../../utils/roles';

const STATUS_BADGE_VARIANT = { present: 'success', absent: 'danger', late: 'warning', excused: 'info' };

function AttendanceRegisterView({ token, user }) {
  const canFilterByClass = hasAnyRole(user, ['administrator', 'principal', 'head_of_academics', 'teacher']);
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [registers, setRegisters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'success') => setToasts(prev => [...prev, { message, type }]);
  const removeToast = (index) => setToasts(prev => prev.filter((_, i) => i !== index));

  useEffect(() => {
    if (!canFilterByClass) return;
    fetch('/api/v1/classes', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setClasses)
      .catch(() => {});
  }, [token, canFilterByClass]);

  const fetchRegisters = useCallback(async () => {
    setLoading(true);
    try {
      const url = classId ? `/api/v1/attendance/registers?class_id=${classId}` : '/api/v1/attendance/registers';
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        setRegisters(await response.json());
      } else {
        addToast(await safeErrorMessage(response, 'Failed to load registers'), 'error');
      }
    } catch {
      addToast('Network error. Please check your connection and try again.', 'error');
    } finally {
      setLoading(false);
    }
  }, [token, classId]);

  useEffect(() => { fetchRegisters(); }, [fetchRegisters]);

  const viewDetail = async (registerId) => {
    try {
      const response = await fetch(`/api/v1/attendance/registers/${registerId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        setDetail(await response.json());
      } else {
        addToast(await safeErrorMessage(response, 'Failed to load register detail'), 'error');
      }
    } catch {
      addToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  const columns = [
    { header: 'Date', accessor: 'date' },
    { header: 'Class', accessor: 'class_display_name' },
    { header: 'Type', render: (row) => row.session_type === 'homeroom' ? `Homeroom (${row.homeroom_session})` : 'Subject Period' },
    { header: 'Status', render: (row) => <Badge variant={row.status === 'submitted' ? 'success' : 'default'}>{row.status}</Badge> },
    {
      header: 'Actions', width: '80px',
      render: (row) => (
        <Button variant="ghost" size="sm" onClick={() => viewDetail(row.id)}>
          <EyeIcon style={{ width: 14, height: 14 }} />
        </Button>
      ),
    },
  ];

  if (loading) return <LoadingSkeleton type="text" rows={10} />;

  return (
    <div>
      {canFilterByClass && (
        <div className="form-group" style={{ maxWidth: '260px', marginBottom: '1rem' }}>
          <label>Filter by Class</label>
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            <option value="">All Classes</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.display_name}</option>)}
          </select>
        </div>
      )}

      <Card title="Attendance Registers">
        <Table columns={columns} data={registers} emptyMessage="No attendance registers found." />
      </Card>

      <Modal isOpen={Boolean(detail)} onClose={() => setDetail(null)} title={detail ? `${detail.class_display_name} — ${detail.date}` : ''}>
        {detail && (
          <div>
            {detail.session_type === 'subject_period' && (detail.topic_taught || detail.homework_assigned) && (
              <div style={{ marginBottom: '1rem', fontSize: '0.85rem' }}>
                {detail.topic_taught && <p><strong>Topic:</strong> {detail.topic_taught}</p>}
                {detail.homework_assigned && <p><strong>Homework:</strong> {detail.homework_assigned}</p>}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {(detail.records || []).map(r => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0.75rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{r.student_name}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    {r.remarks && <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{r.remarks}</span>}
                    <Badge variant={STATUS_BADGE_VARIANT[r.status] || 'default'}>{r.status}</Badge>
                  </div>
                </div>
              ))}
              {(!detail.records || detail.records.length === 0) && (
                <p style={{ color: 'var(--color-text-muted)' }}>No records in this register yet.</p>
              )}
            </div>
          </div>
        )}
      </Modal>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

export default AttendanceRegisterView;
